import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { saveBlobDownload } from '../../../shared/utils/saveBlobDownload';
import { savePdfDocument } from '../../../super-calculator/utils/savePdfDocument';
import { logBestPricePromoShared } from '../../services/analytics';
import {
  buildBestPricePromoPdf,
  renderPdfToJpegBlobs,
  type BestPricePromoRow,
} from './bestPricePromoExport';

interface BestPricePromoShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** One row per project — highest promo first (see `toBestPricePromoRows`). */
  rows: BestPricePromoRow[];
  agentLine: string;
  agencyLine: string;
  /** Active filter summary shown under the title, e.g. `3BR · PH only`. */
  filterNote?: string;
  ceaNumber?: string | null;
}

const fileStamp = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * "Best Price Promo" sheet — the deepest promo of every project in the current
 * Price Promo Units view, rendered as an A4 PDF and rasterised to a JPEG the
 * agent can forward straight to clients.
 *
 * The PDF is only the intermediate: the download is the JPEG, delivered through
 * `saveBlobDownload` so it keeps its filename inside the native WebView. If
 * pdf.js cannot rasterise (worker blocked / low memory), the PDF itself is
 * offered instead of failing the whole action.
 */
const BestPricePromoShareDialog: React.FC<BestPricePromoShareDialogProps> = ({
  open,
  onClose,
  rows,
  agentLine,
  agencyLine,
  filterNote,
  ceaNumber,
}) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Object URLs for the preview <img> — one per page, revoked on regenerate. */
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const blobsRef = useRef<Blob[]>([]);
  const previewUrlsRef = useRef<string[]>([]);
  /** Set when rasterising failed and the PDF is the only thing we can hand over. */
  const pdfFallbackRef = useRef<(() => Promise<void>) | null>(null);

  const releasePreviews = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setPreviewUrls([]);
  }, []);

  useEffect(() => () => releasePreviews(), [releasePreviews]);

  // Generate once per opening — the underlying rows are fixed while the sheet
  // is on screen (the dialog sits above the Price Promo dialog's filters).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setGenerating(true);
    setError(null);
    setPageIndex(0);
    releasePreviews();
    blobsRef.current = [];
    pdfFallbackRef.current = null;

    void (async () => {
      try {
        const doc = await buildBestPricePromoPdf({ rows, agentLine, agencyLine, filterNote });
        if (cancelled) return;

        let blobs: Blob[];
        try {
          blobs = await renderPdfToJpegBlobs(doc);
        } catch (rasterErr) {
          console.error('Best Price Promo: could not rasterise the PDF, offering the PDF instead:', rasterErr);
          if (cancelled) return;
          pdfFallbackRef.current = () =>
            savePdfDocument(doc, `best-price-promo-${fileStamp()}.pdf`);
          setError('Could not build the image on this device — you can download the PDF instead.');
          return;
        }
        if (cancelled) return;

        blobsRef.current = blobs;
        const urls = blobs.map((blob) => URL.createObjectURL(blob));
        previewUrlsRef.current = urls;
        setPreviewUrls(urls);
        logBestPricePromoShared({
          stage: 'preview',
          totalProjects: rows.length,
          pageCount: blobs.length,
          topSaving: rows[0]?.saving,
          ceaNumber,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to generate the Best Price Promo sheet:', err);
        setError('Could not generate the sheet. Please try again.');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `rows` / agent lines are snapshotted at open; regenerating on every render
    // of the parent would rebuild the PDF continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDownload = useCallback(async () => {
    setSaving(true);
    try {
      if (pdfFallbackRef.current) {
        await pdfFallbackRef.current();
        return;
      }
      const blobs = blobsRef.current;
      if (!blobs.length) return;
      const stamp = fileStamp();
      // One file per page; a single-page sheet keeps the plain name.
      for (let i = 0; i < blobs.length; i += 1) {
        const suffix = blobs.length > 1 ? `-p${i + 1}` : '';
        await saveBlobDownload(blobs[i], `best-price-promo-${stamp}${suffix}.jpg`, 'image/jpeg');
      }
      logBestPricePromoShared({
        stage: 'download',
        totalProjects: rows.length,
        pageCount: blobs.length,
        topSaving: rows[0]?.saving,
        ceaNumber,
      });
    } catch (err) {
      console.error('Failed to save the Best Price Promo image:', err);
      setError('Could not save the image. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [ceaNumber, rows]);

  const pageCount = previewUrls.length;
  const canDownload = !generating && (pageCount > 0 || Boolean(pdfFallbackRef.current));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'var(--color-surface)',
          color: 'rgba(var(--color-text-rgb), 0.9)',
          border: '1px solid rgba(var(--color-text-rgb), 0.08)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
              Best Price Promo
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              {rows.length} project{rows.length === 1 ? '' : 's'} · highest promo each
              {filterNote ? ` · ${filterNote}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'var(--color-surface)', borderColor: 'rgba(var(--color-text-rgb), 0.08)' }}>
        {error && (
          <Alert severity={pdfFallbackRef.current ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '210 / 297',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(var(--color-shadow-rgb), 0.25)',
            border: '1px solid rgba(var(--color-text-rgb), 0.1)',
          }}
        >
          {pageCount > 0 ? (
            <Box
              component="img"
              src={previewUrls[Math.min(pageIndex, pageCount - 1)]}
              alt={`Best Price Promo page ${pageIndex + 1}`}
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : generating ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress />
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                Building your sheet…
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ p: 3, textAlign: 'center', color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              No preview available.
            </Typography>
          )}

          {pageCount > 1 && (
            <>
              <IconButton
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                disabled={pageIndex === 0}
                size="small"
                sx={{ position: 'absolute', left: 8, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
                disabled={pageIndex >= pageCount - 1}
                size="small"
                sx={{ position: 'absolute', right: 8, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Page {pageIndex + 1} of {pageCount}
              </Typography>
            </>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          Saved as a JPEG image{pageCount > 1 ? ` (${pageCount} pages, one file each)` : ''} with your
          CEA details in the footer.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
          Close
        </Button>
        <Button
          onClick={handleDownload}
          disabled={!canDownload || saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        >
          {pdfFallbackRef.current ? 'Download PDF' : pageCount > 1 ? `Download ${pageCount} JPGs` : 'Download JPG'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BestPricePromoShareDialog;
